import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useEnterAsTab } from '../hooks/useEnterAsTab';
import { AppModal } from '../components/AppModal';

const Row = ({ label, children }: any) => (
  <div className="flex items-center mb-2">
    <span className="w-[220px] text-[13px] text-gray-800 flex-shrink-0">{label}</span>
    <span className="w-[14px] text-center font-bold text-gray-700 flex-shrink-0">:</span>
    <div className="flex-1 min-w-0">{children}</div>
  </div>
);

export const CurrencyCreationView = ({ config, handleItemSelect, currencyId }: any) => {
  const containerRef  = useRef<HTMLDivElement>(null);
  const nameInputRef  = useRef<HTMLInputElement>(null);
  const activeCompany = useStore((state) => state.activeCompany);

  const [name, setName]                     = useState('');
  const [symbol, setSymbol]                 = useState('');
  const [code, setCode]                     = useState('');
  const [isBaseCurrency, setIsBaseCurrency] = useState(false);
  const [exchangeRate, setExchangeRate]     = useState<number | ''>('');
  const [isFetchingRate, setIsFetchingRate] = useState(false);
  const [accepting, setAccepting]           = useState(false);

  useEnterAsTab(containerRef);

  useEffect(() => {
    if (!accepting) return;
    const h = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'y' || e.key === 'Enter') { e.preventDefault(); e.stopImmediatePropagation(); doSave(); }
      else if (e.key.toLowerCase() === 'n' || e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); setAccepting(false); }
    };
    window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [accepting]);

  useEffect(() => {
    if (currencyId) {
      db.currencies.get(currencyId).then(c => {
        if (c) { setName(c.name); setSymbol(c.symbol); setCode(c.code); setIsBaseCurrency(c.isBaseCurrency); setExchangeRate(c.exchangeRate); }
      });
    } else { setName(''); setSymbol(''); setCode(''); setIsBaseCurrency(false); setExchangeRate(''); }
  }, [currencyId]);

  useEffect(() => { if (nameInputRef.current) nameInputRef.current.focus(); }, [currencyId]);
  useEffect(() => { if (isBaseCurrency) setExchangeRate(1); }, [isBaseCurrency]);

  const handleFetchRate = async () => {
    if (!code || isBaseCurrency) return;
    try {
      setIsFetchingRate(true);
      const baseCur = await db.currencies.where('companyId').equals(activeCompany!.id!).filter(c => c.isBaseCurrency).first();
      if (!baseCur) { alert('No base currency defined.'); setIsFetchingRate(false); return; }
      const res = await fetch(`https://open.er-api.com/v6/latest/${code.trim().toUpperCase()}`);
      const data = await res.json();
      if (data.result === 'success') {
        const rate = data.rates[baseCur.code];
        if (rate) setExchangeRate(rate); else alert(`Could not find rate for ${baseCur.code}`);
      } else alert('Failed to fetch from API');
    } catch (e: any) { alert(e?.message || 'Error fetching rate'); } finally { setIsFetchingRate(false); }
  };

  const doSave = async () => {
    setAccepting(false);
    if (!name.trim() || !symbol.trim() || !code.trim() || !activeCompany) { alert('Name, Symbol, and Code are required'); return; }
    if (exchangeRate === '' || Number(exchangeRate) <= 0) { alert('Exchange rate must be a positive number'); return; }
    try {
      const existingCurrencies = await db.currencies.where('companyId').equals(activeCompany.id!).toArray();
      const codeExists = existingCurrencies.some(c => c.code.toLowerCase() === code.trim().toLowerCase() && c.id !== currencyId);
      if (codeExists) { alert('A currency with this code already exists.'); return; }
      await db.transaction('rw', [db.currencies, db.auditLogs], async () => {
        if (isBaseCurrency) {
          const prev = existingCurrencies.filter(c => c.isBaseCurrency && c.id !== currencyId);
          for (const c of prev) await db.currencies.update(c.id!, { isBaseCurrency: false });
        }
        let auditAction: 'CREATE' | 'UPDATE' = 'UPDATE';
        let affectedId = currencyId;
        if (currencyId) {
          await db.currencies.update(currencyId, { name: name.trim(), symbol: symbol.trim(), code: code.trim().toUpperCase(), isBaseCurrency, exchangeRate: Number(exchangeRate) });
        } else {
          auditAction = 'CREATE';
          const exists = await db.currencies.where('[companyId+code]').equals([activeCompany.id!, code.trim().toUpperCase()]).first();
          if (exists) throw new Error('DUPLICATE_CURRENCY_CODE');
          affectedId = await db.currencies.add({ companyId: activeCompany.id!, name: name.trim(), symbol: symbol.trim(), code: code.trim().toUpperCase(), isBaseCurrency, exchangeRate: Number(exchangeRate), createdAt: new Date().toISOString() }) as number;
        }
        await db.auditLogs.add({ companyId: activeCompany.id!, actionType: auditAction, entityType: 'currency', entityId: affectedId!, timestamp: new Date().toISOString(), details: `${auditAction === 'CREATE' ? 'Created' : 'Updated'} currency ${name.trim()} (${code.trim().toUpperCase()})` });
      });
      if (currencyId) {
        if (handleItemSelect) handleItemSelect({ name: 'Quit' });
      } else {
        setName(''); setSymbol(''); setCode(''); setIsBaseCurrency(false); setExchangeRate('');
        setTimeout(() => nameInputRef.current?.focus(), 100);
      }
    } catch (error: any) {
      if (error?.message === 'DUPLICATE_CURRENCY_CODE') { alert('Currency code already exists'); return; }
      alert(error?.message || 'Failed to save currency');
    }
  };

  const handleSave = () => setAccepting(true);

  const [pendingDelete, setPendingDelete] = useState(false);

  const handleDelete = async () => {
    if (!currencyId || isBaseCurrency || !activeCompany) { if (isBaseCurrency) alert('Cannot delete the Base Currency.'); return; }
    try {
      const inUse = await db.vouchers.where({ companyId: activeCompany.id! }).filter(v => v.currencyId === currencyId).count();
      if (inUse > 0) { alert('Cannot delete this currency because it is used in existing transactions.'); return; }
      setPendingDelete(true);
    } catch (error: any) { alert(error?.message || 'Failed to delete currency'); }
  };

  const confirmDelete = async () => {
    setPendingDelete(false);
    if (!currencyId || !activeCompany) return;
    try {
      await db.transaction('rw', [db.currencies, db.auditLogs], async () => {
        await db.currencies.update(currencyId, { isDeleted: true, deletedAt: new Date().toISOString(), deletedBy: 'System' } as any);
        await db.auditLogs.add({ companyId: activeCompany.id!, actionType: 'DELETE', entityType: 'currency', entityId: currencyId, timestamp: new Date().toISOString(), details: `Deleted currency ${name} (${code})` });
      });
      if (handleItemSelect) handleItemSelect({ name: 'Quit' });
    } catch (error: any) { alert(error?.message || 'Failed to delete currency'); }
  };

  const quit = () => { if (handleItemSelect) handleItemSelect({ name: 'Quit' }); };


  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm font-sans text-[13px]">
      <div ref={containerRef} className="flex flex-col bg-white relative overflow-hidden border border-[#444] rounded-sm"
        style={{ width: '680px', maxWidth: '98vw', maxHeight: '94vh' }}>

        <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px] flex-shrink-0">
          <span className="w-1/3 text-left">{currencyId ? 'Currency Alteration' : 'Currency Creation'}</span>
          <span className="w-1/3 text-center uppercase">{activeCompany?.name || ''}</span>
          <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" data-quit-btn onClick={quit}>✕</span>
        </div>

        <div className="flex border-b border-[#c8d4d4] bg-[#f9fafb] flex-shrink-0 p-4">
          <div className="flex items-center w-full">
            <span className="w-[150px] flex-shrink-0">Name</span>
            <span className="w-[14px] text-center font-bold flex-shrink-0">:</span>
            <input ref={nameInputRef} type="text" value={name} onChange={e => setName(e.target.value)}
              className="flex-1 max-w-[380px] border border-[#a0c5e8] bg-[#fff9c4] px-1 py-[2px] outline-none shadow-inner text-black font-semibold"
              autoFocus />
          </div>
        </div>

        <div className="flex-1 p-4 overflow-y-auto bg-[#fdfdfd]">
          <Row label="Symbol (e.g. ₹, $)">
            <input type="text" value={symbol} onChange={e => setSymbol(e.target.value)}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50" />
          </Row>
          <Row label="Code (e.g. INR, USD)">
            <input type="text" value={code} onChange={e => setCode(e.target.value)}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50 uppercase" />
          </Row>
          <Row label="Is Base Currency">
            <select value={isBaseCurrency ? 'Yes' : 'No'} onChange={e => setIsBaseCurrency(e.target.value === 'Yes')}
              className="border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-full max-w-[200px] focus:bg-yellow-50">
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </Row>
          <Row label="Exchange Rate (to Base)">
            <div className="flex items-center gap-2">
              {!isBaseCurrency && (
                <button type="button" onClick={handleFetchRate} disabled={isFetchingRate || !code}
                  className="text-[11px] bg-blue-100 text-blue-700 px-2 border border-blue-300 rounded hover:bg-blue-200 disabled:opacity-50">
                  {isFetchingRate ? '...' : 'Fetch Live API'}
                </button>
              )}
              <input type="number" step="0.0001" value={exchangeRate} onChange={e => setExchangeRate(e.target.value ? Number(e.target.value) : '')}
                disabled={isBaseCurrency}
                className={`border-b border-gray-300 outline-none bg-transparent text-black font-semibold w-[120px] focus:bg-yellow-50 text-right ${isBaseCurrency ? 'opacity-60 cursor-not-allowed' : ''}`} />
            </div>
          </Row>
        </div>

        <div className="flex justify-between items-center bg-[#ebebeb] border-t border-[#b8b8b8] px-4 py-2 text-[12.5px] flex-shrink-0 h-[33px]">
          <div />
          <div className="flex items-center ml-auto gap-2">
            {currencyId && (
              <button type="button" onClick={handleDelete} tabIndex={-1}
                className="px-3 py-0.5 border border-gray-400 text-gray-700 bg-[#e0e0e0] hover:bg-[#d0d0d0] rounded text-[11.5px] font-semibold"
                title="Alt + D to Delete"><u>D</u>elete</button>
            )}
            <button type="button" onClick={handleSave} tabIndex={-1} className="px-4 py-1 bg-[#1e8585] text-white font-bold rounded text-[11.5px] hover:bg-[#166e6e] shadow-sm mr-2">✓ Accept (Ctrl+A)</button><span className="text-gray-500 italic text-[11px]">Enter = Next · Esc = Cancel</span>
          </div>
        </div>

        {accepting && (
          <div className="absolute inset-0 bg-black/10 z-50 flex items-center justify-center">
            <div className="border-2 border-[#1e8585] bg-white px-16 py-6 shadow-[8px_8px_0_rgba(30,133,133,0.25)] text-center min-w-[200px] rounded-sm">
              <span className="block text-[17px] font-bold mb-4 text-black">Accept ?</span>
              <div className="font-bold text-[15px]">
                <span className="text-[#1e8585] cursor-pointer hover:underline" onClick={doSave}><u>Y</u>es</span>
                &nbsp;&nbsp;or&nbsp;&nbsp;
                <span className="text-gray-500 cursor-pointer hover:underline" onClick={() => setAccepting(false)}><u>N</u>o</span>
              </div>
            </div>
          </div>
        )}
        <button data-save-btn onClick={handleSave} className="hidden" />

        <AppModal
          open={pendingDelete}
          title="Delete Currency"
          type="warning"
          message={`Are you sure you want to delete currency: ${name}?`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(false)}
        />
      </div>
    </div>
  );
};
